import {
  cartActions,
  defineElement,
  getCartItemProperties,
  getCartObject,
  getCartSectionsToRender,
  getLineItemFeeProducts,
  getLineItemObjectFeeProducts,
  getSameCorporateGifts,
} from 'utils';

import { lineItemPropStrings } from 'lib/constants';

export class CartRemoveBase extends HTMLElement {
  constructor() {
    super();

    this.productType = this.getAttribute('data-product-type');
    this.isPhysicalProduct = this.productType === 'physical';

    this.addEventListener('click', (event) => {
      this.handleRemove(event);
    });
  }

  async removeCartItems(body) {
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

    try {
      cartItems.enableLoading();

      const response = await cartActions.update(body);

      if (!response.ok) {
        throw new Error('Error removing line items');
      }

      const state = await response.text();
      cartItems.renderCartItems(state);
    } catch (error) {
      console.error(error);
    } finally {
      cartItems.disableLoading();
    }
  }
}

export class CartRemoveButton extends CartRemoveBase {
  constructor() {
    super();
  }

  handleRemove(event) {
    event.preventDefault();
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
    const cartObject = getCartObject(cartItems);
    const cartItem = this.closest('.cart-item');
    const { properties, key } = getCartItemProperties(cartItem);

    const body = {
      updates: {
        [key]: 0,
      },
      sections: getCartSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    // Update the physicalItemsCount attribute if the product is physical
    if (this.isPhysicalProduct) {
      body.attributes = {
        physicalItemsCount: cartObject.totalPhysicalBoards - 1,
      };
    }

    const feeProducts = getLineItemFeeProducts(cartItem);
    if (feeProducts.length) {
      feeProducts.forEach((product) => {
        body.updates[product.key] = 0;
      });
    }

    // If the product is a corporate gift and there are two corporate gifts in the cart
    if (properties[lineItemPropStrings.corporateGift]) {
      const corporateGifts = getSameCorporateGifts(cartObject.items, properties[lineItemPropStrings.corporateGroupKey]);

      if (corporateGifts.length === 2) {
        corporateGifts.forEach((product) => {
          body.updates[product.key] = 0;

          const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
          const cartItem = cartItems.querySelector(`.cart-item[data-key="${product.key}"]`);
          const feeProducts = getLineItemFeeProducts(cartItem);

          if (feeProducts.length) {
            feeProducts.forEach((product) => {
              body.updates[product.key] = 0;
            });
          }
        });
      }
    }

    // If the product is a premium package and there are no other items in the cart, remove the premium package as well
    const premiumPackageProduct = cartObject.items.find((item) => item.handle.includes('protection'));
    if (premiumPackageProduct && cartObject.item_count - 1 === Object.keys(body.updates).length) {
      if (premiumPackageProduct) {
        body.updates[premiumPackageProduct.key] = 0;
      }
    }

    this.removeCartItems(JSON.stringify(body));
  }
}

defineElement('cart-remove-button', CartRemoveButton);

export class CartRemoveButtonBulk extends CartRemoveBase {
  constructor() {
    super();
  }

  handleRemove(event) {
    event.preventDefault();
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
    const cartObject = getCartObject(cartItems);
    const cartItemGroup = this.closest('.cart-items-group');
    let cartItemGroupItems = null;

    if (cartItemGroup) {
      cartItemGroupItems = cartItemGroup.querySelectorAll('.cart-item--only-properties');
    } else {
      const cartItem = this.closest('.cart-item');
      const currentItemGroupKey = getCartItemProperties(cartItem).properties[lineItemPropStrings.corporateGroupKey];

      cartItemGroupItems = cartObject.items.filter((item) => {
        if (item.properties[lineItemPropStrings.corporateGroupKey] === currentItemGroupKey) {
          console.log(item);
          item['dataset'] = {
            key: item.key,
          };

          return item;
        }
      });
    }

    const body = {
      updates: {},
      sections: getCartSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    // Update the physicalItemsCount attribute if the parent product is physical
    if (this.isPhysicalProduct) {
      body.attributes = {
        physicalItemsCount: cartObject.totalPhysicalBoards - cartItemGroupItems.length,
      };
    }

    cartItemGroupItems.forEach((cartItem) => {
      const key = cartItem.dataset.key;

      let feeProducts = [];

      if (cartItemGroup) {
        feeProducts = getLineItemFeeProducts(cartItem);
      } else {
        feeProducts = getLineItemObjectFeeProducts(cartObject, cartItemGroupItems);
      }

      if (feeProducts.length) {
        feeProducts.forEach((product) => {
          body.updates[product.key] = 0;
        });
      }

      body.updates[key] = 0;
    });

    // If the product is a premium package and there are no other items in the cart, remove the premium package as well
    const premiumPackageProduct = cartObject.items.find((item) => item.handle.includes('protection'));
    if (premiumPackageProduct && cartObject.item_count - 1 === Object.keys(body.updates).length) {
      if (premiumPackageProduct) {
        body.updates[premiumPackageProduct.key] = 0;
      }
    }

    this.removeCartItems(JSON.stringify(body));
  }
}

defineElement('cart-remove-button-bulk', CartRemoveButtonBulk);
